// ABOUTME: Assigns company users to WhatsApp pre-registration routing scopes.
// ABOUTME: Keeps unavailable users visible until an administrator removes them.
import React from "react";
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    TextField,
    Tooltip,
    Typography
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";

import { WhatsappPreRegistrationLabels } from "./whatsappPreRegistrationLabels";
import {
    WhatsappPreRegistrationConfig,
    WhatsappPreRegistrationOptions
} from "./whatsappPreRegistrationSettingsModel";

interface WhatsappPreRegistrationAssignmentEditorProps {
    config: WhatsappPreRegistrationConfig;
    options: WhatsappPreRegistrationOptions;
    labels: WhatsappPreRegistrationLabels;
    onChange: (config: WhatsappPreRegistrationConfig) => void;
}

const getSelectValues = (value: unknown) =>
    (Array.isArray(value) ? value : String(value || "").split(","))
        .map((item) => String(item || "").trim())
        .filter(Boolean);

const WhatsappPreRegistrationAssignmentEditor = ({
    config,
    options,
    labels,
    onChange
}: WhatsappPreRegistrationAssignmentEditorProps) => {
    const updateAssignment = (
        index: number,
        changes: Partial<
            WhatsappPreRegistrationConfig["userAssignments"][number]
        >
    ) => {
        onChange({
            ...config,
            userAssignments: config.userAssignments.map(
                (assignment, assignmentIndex) =>
                    assignmentIndex === index
                        ? { ...assignment, ...changes }
                        : assignment
            )
        });
    };
    const configuredStoreIds = new Set(
        config.routingMode === "BRANCH"
            ? config.branchIds
            : config.regions.reduce<string[]>(
                  (storeIds, region) => [...storeIds, ...region.storeIds],
                  []
              )
    );
    const configuredStores = options.stores.filter((store) =>
        configuredStoreIds.has(store.id)
    );

    return (
        <Paper elevation={0} style={{ padding: 24 }}>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
            >
                <Typography variant="h6">{labels.assignments}</Typography>
                <Button
                    startIcon={<AddIcon />}
                    onClick={() =>
                        onChange({
                            ...config,
                            userAssignments: [
                                ...config.userAssignments,
                                {
                                    userEmail: "",
                                    regionIds: [],
                                    storeIds: []
                                }
                            ]
                        })
                    }
                    color="primary"
                >
                    {labels.addAssignment}
                </Button>
            </Box>
            <Typography color="textSecondary" variant="body2" paragraph>
                {labels.assignmentsHelp}
            </Typography>
            {config.userAssignments.length === 0 && (
                <Alert severity="info">{labels.noAssignments}</Alert>
            )}
            {config.userAssignments.map((assignment, index) => {
                const usersAssignedElsewhere = new Set(
                    config.userAssignments
                        .filter(
                            (_, assignmentIndex) => assignmentIndex !== index
                        )
                        .map((item) => item.userEmail)
                );
                return (
                    <Box
                        key={index}
                        mb={2}
                        p={2}
                        border="1px solid #e0e0e0"
                        borderRadius={4}
                    >
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={3}>
                                <TextField
                                    select
                                    fullWidth
                                    variant="outlined"
                                    label={labels.user}
                                    value={assignment.userEmail}
                                    onChange={(event) =>
                                        updateAssignment(index, {
                                            userEmail: String(
                                                event.target.value || ""
                                            ).toLowerCase()
                                        })
                                    }
                                >
                                    <MenuItem value="">-</MenuItem>
                                    {options.users.map((user) => (
                                        <MenuItem
                                            key={user.email}
                                            value={user.email}
                                            disabled={usersAssignedElsewhere.has(
                                                user.email
                                            )}
                                        >
                                            {user.name}
                                            {user.available === false
                                                ? ` (${labels.unavailable})`
                                                : ""}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            {config.routingMode === "REGION_BRANCH" && (
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth variant="outlined">
                                        <InputLabel>
                                            {labels.regionsForUser}
                                        </InputLabel>
                                        <Select
                                            multiple
                                            value={assignment.regionIds}
                                            onChange={(event) =>
                                                updateAssignment(index, {
                                                    regionIds: getSelectValues(
                                                        event.target.value
                                                    )
                                                })
                                            }
                                            label={labels.regionsForUser}
                                            renderValue={(selected) =>
                                                getSelectValues(selected)
                                                    .map(
                                                        (regionId) =>
                                                            config.regions.find(
                                                                (region) =>
                                                                    region.id ===
                                                                    regionId
                                                            )?.name || regionId
                                                    )
                                                    .join(", ")
                                            }
                                        >
                                            {config.regions.map((region) => (
                                                <MenuItem
                                                    key={region.id}
                                                    value={region.id}
                                                >
                                                    <Checkbox
                                                        checked={assignment.regionIds.includes(
                                                            region.id
                                                        )}
                                                    />
                                                    <ListItemText
                                                        primary={
                                                            region.name ||
                                                            region.id
                                                        }
                                                    />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                            <Grid
                                item
                                xs={12}
                                md={
                                    config.routingMode === "REGION_BRANCH"
                                        ? 4
                                        : 8
                                }
                            >
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel>
                                        {labels.storesForUser}
                                    </InputLabel>
                                    <Select
                                        multiple
                                        value={assignment.storeIds}
                                        onChange={(event) =>
                                            updateAssignment(index, {
                                                storeIds: getSelectValues(
                                                    event.target.value
                                                )
                                            })
                                        }
                                        label={labels.storesForUser}
                                        renderValue={(selected) =>
                                            getSelectValues(selected)
                                                .map(
                                                    (storeId) =>
                                                        configuredStores.find(
                                                            (store) =>
                                                                store.id ===
                                                                storeId
                                                        )?.name || storeId
                                                )
                                                .join(", ")
                                        }
                                    >
                                        {configuredStores.map((store) => (
                                            <MenuItem
                                                key={store.id}
                                                value={store.id}
                                            >
                                                <Checkbox
                                                    checked={assignment.storeIds.includes(
                                                        store.id
                                                    )}
                                                />
                                                <ListItemText
                                                    primary={`${store.name}${
                                                        store.available ===
                                                        false
                                                            ? ` (${labels.unavailable})`
                                                            : ""
                                                    }`}
                                                />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={1}>
                                <Tooltip title={labels.delete}>
                                    <IconButton
                                        onClick={() =>
                                            onChange({
                                                ...config,
                                                userAssignments: config.userAssignments.filter(
                                                    (_, assignmentIndex) =>
                                                        assignmentIndex !==
                                                        index
                                                )
                                            })
                                        }
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Box>
                );
            })}
        </Paper>
    );
};

export default WhatsappPreRegistrationAssignmentEditor;
